import React, {
  createRef,
  useEffect,
  useState,
  useRef,
  useCallback,
} from "react";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import FormControl from "@mui/material/FormControl";
import RadioGroup from "@mui/material/RadioGroup";
import FormHelperText from "@mui/material/FormHelperText";
import FormLabel from "@mui/material/FormLabel";
import { Paper, Typography, Tooltip, Box } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import EditIcon from "@mui/icons-material/Edit";
import { Checkbox, ListItemText, Chip } from "@mui/material";
import MenuItem from "@mui/material/MenuItem";
import _ from "lodash";
import parse from "date-fns/parse";
import { createFilterOptions } from "@mui/material/Autocomplete";

const DEFAULT_DATE_FORMAT = "mm/dd/yy";

const inputSx = {
  "& .MuiInputLabel-root": {
    fontSize: [".5rem", ".5rem", ".8rem", ".8rem", "1.15rem", "1.15rem"],
    transform: {
      md: "translate(12px, 10px) scale(1)",
      lg: "translate(12px, 16px) scale(1)",
    },
  },
  "& .MuiInputLabel-shrink": {
    transform: {
      md: "translate(12px, 3px) scale(0.75)",
      lg: "translate(12px, 7px) scale(0.75)",
    },
  },
  "& .MuiFilledInput-root": {
    pt: [".75rem", ".75rem", ".75rem", ".75rem", "19px", "19px"],
    fontSize: [".5rem", ".5rem", ".75rem", ".75rem", "1rem", "1rem"],
  },
  "& .MuiFilledInput-input": {
    // m: ['0rem', '0rem', '0rem', '0rem', '.2rem', '.2rem'],
    paddingBottom: [
      "0.335rem",
      "0.335rem ",
      "0.335rem !important",
      "0.335rem !important",
      "0.5rem",
      "0.5rem",
    ],
  },
  "& .MuiAutocomplete-input": {
    px: [
      "3px !important",
      "3px !important",
      "3px !important",
      "3px !important",
      "5px !important",
      "5px !important",
    ],
    // py: "5px !important",
    py: [
      "5px !important",
      "5px !important",
      "5px !important",
      "5px !important",
      "7px !important",
      "7px !important",
    ],
    lineHeight: ["1rem", "1rem", "1rem", "1rem", "1.4375rem", "1.4375rem"],
    height: ["1rem", "1rem", "1rem", "1rem", "1.4375rem", "1.4375rem"],
  },
  "& .MuiSvgIcon-root": {
    fontSize: [
      "1rem !important",
      "1rem !important",
      "1rem !important",
      "1rem !important",
      "1.5rem !important",
      "1.5rem !important",
    ],
  },
  "& .MuiAutocomplete-endAdornment": {
    right: "9px",
  },
  "& .MuiFormHelperText-root": {
    fontSize: ["0.5rem", "0.5rem", "0.5rem", "0.5rem", "0.75rem", "0.75rem"],
  },
};

const textAreaSx = {
  "& .MuiInputLabel-root": {
    fontSize: [".5rem", ".5rem", ".8rem", ".8rem", "1.15rem", "1.15rem"],
    transform: {
      md: "translate(12px, 10px) scale(1)",
      lg: "translate(12px, 16px) scale(1)",
    },
  },
  "& .MuiInputLabel-shrink": {
    transform: {
      md: "translate(12px, 3px) scale(0.75)",
      lg: "translate(12px, 7px) scale(0.75)",
    },
    backgroundColor: "transparent",
  },
  "& .MuiInputBase-input": {
    paddingTop: "0rem",
    paddingBottom: "0rem",
  },
  "& .MuiFilledInput-root": {
    paddingTop: ["1.0rem", "1.0rem", "1.0rem", "1.0rem", "25px", "25px"],
    paddingBottom: ["5px", "5px", "5px", "5px", "8px", "8px"],
    fontSize: [".5rem", ".5rem", ".75rem", ".75rem", "1rem", "1rem"],
    lineHeight: ["1rem", "1rem", "1rem", "1rem", "1.4375rem", "1.4375rem"],
  },
  "& .MuiFormHelperText-root": {
    fontSize: ["0.5rem", "0.5rem", "0.5rem", "0.5rem", "0.75rem", "0.75rem"],
  },
};

const specimenSx = {
  fontSize: [".75rem", ".75rem", ".75rem", "0.75rem", "1.125rem", "1.125rem"],
};

const institutionIdSx = {
  maxWidth: ["8rem", "8rem", "10rem", "10rem", "12rem", "12rem"],
};

const autoCompleteTextAreaSx = {
  ...textAreaSx,
  "& .MuiFilledInput-input": {
    pt: "0 !important",
    pb: "0 !important",
  },
};

export const renderInput = (inputProps) => {
  const {
    input,
    style,
    label,
    meta,
    disabled,
    hideFormErrors,
    inputProps: extraProps,
    // eslint-disable-next-line no-unused-vars
    helperText,
    testId,
  } = inputProps;
  const error =
    !hideFormErrors && (meta?.touched || meta?.submitFailed) && meta?.error
      ? Array.isArray(meta.error)
        ? meta.error[0]
        : meta.error
      : undefined;
  return (
    <TextField
      {...input}
      style={{ ...style }}
      label={label}
      disabled={disabled}
      variant="filled"
      autoComplete="off"
      error={error ? true : false}
      helperText={error}
      {...extraProps}
      inputProps={{ "data-testid": testId }}
      sx={textAreaSx}
    />
  );
};

export const RenderSelectForm = (inputProps) => {
  //eslint-disable-next-line
  const {
    input,
    label,
    meta,
    disabled,
    hideFormErrors,
    formControlProps,
    options,
    getOptionLabel,
    filterOptions,
    style,
  } = inputProps;
  const error =
    !hideFormErrors && (meta?.touched || meta?.submitFailed) && meta?.error
      ? Array.isArray(meta.error)
        ? meta.error[0]
        : meta.error
      : undefined;

  return (
    <FormControl
      {...formControlProps}
      disabled={disabled}
      error={error}
      style={{ width: "100%" }}
    >
      <Autocomplete
        {...input}
        sx={inputSx}
        style={{ ...style }}
        options={options}
        autoHighlight
        // {...extraProps}
        getOptionLabel={getOptionLabel}
        renderOption={(props, option) => (
          <li {...props} key={option?.id} value={option?.id}>
            {option?.displayName}
          </li>
        )}
        value={input.value || null}
        onChange={(e, newValue) => {
          input.onChange(newValue ? newValue.id : "");
        }}
        isOptionEqualToValue={(option, value) => option?.id === value}
        filterOptions={
          filterOptions ||
          createFilterOptions({
            matchFrom: "any",
            stringify: (option) => option,
          })
        }
        disabled={disabled}
        renderInput={(params) => {
          return (
            <TextField
              {...params}
              // {...input}
              variant="filled"
              autoComplete="off"
              label={label}
              error={error ? true : false}
              helperText={error}
            />
          );
        }}
      />
    </FormControl>
  );
};

export const RenderMultipleAttractantString = (inputProps) => {
  const {
    input,
    label,
    meta,
    disabled,
    hideFormErrors,
    formControlProps,
    options,
    filterOptions,
    style,
  } = inputProps;

  const error = meta?.error
    ? meta?.error?.[0] && !hideFormErrors && (meta.touched || meta.submitFailed)
      ? meta.error[0]
      : meta?.error
        ? meta?.error
        : undefined
    : undefined;

  const [selectedValues, setSelectedValues] = useState(input.value || []);

  // Keep the displayed chips in sync with the redux-form value
  useEffect(() => {
    const next = Array.isArray(input.value) ? input.value : [];
    setSelectedValues((prev) =>
      prev.length === next.length && prev.every((v, i) => v === next[i])
        ? prev
        : next,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input.value]);

  return (
    <FormControl
      {...formControlProps}
      disabled={disabled}
      error={error}
      style={{ width: "100%" }}
    >
      <Autocomplete
        {...input}
        sx={{ width: "100%" }}
        style={{ ...style }}
        options={Array.isArray(options) ? options : []}
        multiple
        autoHighlight
        value={selectedValues || []}
        onChange={(e, newValue) => {
          setSelectedValues(newValue);
          input.onChange(newValue); // newValue is already an array of strings
        }}
        onBlur={(e) => e.preventDefault()}
        isOptionEqualToValue={(option, value) => option === value}
        filterOptions={
          filterOptions ||
          createFilterOptions({
            matchFrom: "any",
            stringify: (option) => option,
          })
        }
        disabled={disabled}
        renderOption={(props, option, { selected }) => (
          <MenuItem {...props} key={option}>
            <Checkbox checked={selected} />
            <ListItemText primary={option} />
          </MenuItem>
        )}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip
              key={option}
              label={option}
              {...getTagProps({ index })}
              style={{ margin: 2 }}
            />
          ))
        }
        renderInput={(params) => (
          <TextField
            {...params}
            variant="filled"
            autoComplete="off"
            label={label}
            error={!!error}
            helperText={error}
          />
        )}
      />
    </FormControl>
  );
};

export const renderAutocomplete = (inputProps) => {
  const {
    input,
    // eslint-disable-next-line no-unused-vars
    value: givenValue,
    // eslint-disable-next-line no-unused-vars
    fieldValue,
    label,
    meta,
    disabled,
    hideFormErrors,
    inputProps: extraProps,
    options,
    helperText,
    multilineFlag = false,
    normalize,
    ...autocompleteprops
  } = inputProps;
  const error =
    !hideFormErrors && (meta?.touched || meta?.submitFailed) && meta?.error
      ? Array.isArray(meta.error)
        ? meta.error[0]
        : meta.error
      : undefined;

  const { onChange, ...rest } = input;
  return (
    <Autocomplete
      freeSolo
      fullWidth={true}
      selectOnFocus
      onChange={(e, newValue) => {
        onChange(newValue);
      }}
      value={input?.value || ""}
      disabled={disabled}
      options={options ? options : []}
      {...autocompleteprops}
      sx={multilineFlag ? autoCompleteTextAreaSx : inputSx}
      PaperComponent={({ children }) => (
        <Paper
          sx={{
            fontSize: [
              ".5rem",
              ".5rem",
              ".8rem",
              ".8rem",
              "1.15rem",
              "1.15rem",
            ],
          }}
        >
          {children}
        </Paper>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          {...rest}
          label={label}
          variant="filled"
          error={error ? true : false}
          helperText={error ? error : helperText}
          onChange={(e, newValue) => {
            onChange(e, newValue);
          }}
          multiline={multilineFlag}
        />
      )}
      {...extraProps}
    />
  );
};

export const RenderSelect = (inputProps) => {
  //eslint-disable-next-line
  const {
    input,
    label,
    meta,
    disabled,
    hideFormErrors,
    formControlProps,
    options,
    getOptionLabel,
    filterOptions,
    noOptionsText,
  } = inputProps;
  const error =
    !hideFormErrors && (meta?.touched || meta?.submitFailed) && meta?.error
      ? Array.isArray(meta.error)
        ? meta.error[0]
        : meta.error
      : undefined;
  return (
    <FormControl
      {...formControlProps}
      disabled={disabled}
      error={error}
      style={{ width: "100%" }}
    >
      <Autocomplete
        {...input}
        sx={inputSx}
        options={options}
        autoHighlight
        noOptionsText={noOptionsText}
        // {...extraProps}
        getOptionLabel={getOptionLabel}
        renderOption={(props, option) => (
          <li {...props} key={option?.id} value={option?.id}>
            {option?.displayName}
          </li>
        )}
        value={input.value || null}
        onChange={(e, newValue) => {
          input.onChange(newValue?.id || "");
        }}
        isOptionEqualToValue={(option, value) => option?.id === value}
        filterOptions={
          filterOptions ||
          createFilterOptions({
            matchFrom: "any",
            stringify: (option) => option,
          })
        }
        disabled={disabled}
        renderInput={(params) => {
          return (
            <TextField
              {...params}
              variant="filled"
              label={label}
              error={!!error}
              helperText={error}
            />
          );
        }}
      />
    </FormControl>
  );
};

export const renderRadioGroup = ({
  input,
  meta,
  label,
  disabled,
  formControlProps,
  hideFormErrors,
  inputProps: extraProps,
  ...rest
}) => {
  const error =
    !hideFormErrors && (meta?.touched || meta?.submitFailed) && meta?.error
      ? Array.isArray(meta.error)
        ? meta.error[0]
        : meta.error
      : undefined;
  return (
    <FormControl
      component="fieldset"
      {...formControlProps}
      variant="filled"
      disabled={disabled}
      error={error}
    >
      <FormLabel disabled={disabled} color="primary">
        {label}
      </FormLabel>
      <RadioGroup {...input} {...extraProps} {...rest} value={input.value} />
      {error && <FormHelperText>{error}</FormHelperText>}
    </FormControl>
  );
};

export const RenderDatePicker = (props) => {
  const {
    input,
    label,
    disabled,
    setHasErrorsFlag,
    cusMinDate = "2018-01-01",
    handleChange,
  } = props;
  const updateParent = _.debounce(setHasErrorsFlag, 200, { trailing: true });
  const [date, setDate] = useState();
  const textFieldRef = useRef();

  const isValidDate = (d) => d instanceof Date && !isNaN(d);

  const autoFillDate = (date) => {
    const rawInput = textFieldRef?.current?.value;
    if (rawInput?.length !== 8 && rawInput?.length !== 10) return;

    const parts = rawInput.split("/");
    if (parts.length !== 3) return;

    const year = parseInt(parts[2]);
    if (isNaN(year) || year < 0 || year >= 100) return;

    if (year >= 10) {
      parts[2] = "20" + year;
      if (new Date(parts.join("/")) > new Date()) parts[2] = "19" + year;
    } else {
      parts[2] = "200" + year;
    }

    const pDate = new Date(parts.join("/"));
    if (!isValidDate(pDate)) return;

    const newDate = parse(
      pDate.toISOString().split("T")[0],
      "yyyy-MM-dd",
      new Date(),
    );
    if (
      !isValidDate(date) ||
      date.toISOString().split("T")[0] !== newDate.toISOString().split("T")[0]
    ) {
      setDate(newDate);
      if (handleChange) handleChange(newDate);
      input.onChange(newDate);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceAutofill = useCallback(
    _.debounce(autoFillDate, 750, { trailing: true }),
    [],
  );

  useEffect(() => {
    debounceAutofill(date);
  }, [date, debounceAutofill]);

  useEffect(() => {
    // update to new date when input.value change
    if (!input.value) {
      setDate(null);
    } else {
      const parsedDate = new Date(input.value);
      if (!isNaN(parsedDate)) {
        const isValidDateState = isValidDate(date);
        if (
          !isValidDateState ||
          parsedDate.toISOString() !== date.toISOString()
        ) {
          setDate(input.value instanceof Date ? input.value : parsedDate);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input.value]);

  const computeError = (value, error) => {
    const curDate = new Date(value);
    const minDate = new Date(cusMinDate);
    const today = new Date();
    if (error) {
      if (date?.toString() === "Invalid Date") {
        updateParent(true);
        if (
          textFieldRef?.current?.value?.length === 8 ||
          textFieldRef?.current?.value?.length === 10
        ) {
          return "Invalid Date Format";
        }
        return DEFAULT_DATE_FORMAT;
      }
      if (minDate > curDate) {
        updateParent(true);
        return "should be after " + cusMinDate;
      } else if (curDate > today) {
        return "should be on or before today";
      } else {
        updateParent(true);
        return DEFAULT_DATE_FORMAT;
      }
    }
    updateParent(false);
    return "";
  };
  return (
    <DatePicker
      label={label}
      minDate={new Date(cusMinDate)}
      maxDate={new Date()}
      {...input}
      value={date || null}
      onChange={(date) => {
        setDate(date);
        input.onChange(date);
        // handleChange(date);
      }}
      inputRef={textFieldRef}
      clearText
      sx={{
        width: "100%",
        "& .MuiInput-root": {
          mt: ["0.5rem", ".5rem", ".5rem", ".5rem", "1rem", "1rem"],
        },
        "& .MuiInputBase-root": {
          fontSize: [".5rem", ".5rem", ".75rem", ".75rem", "1rem", "1rem"],
        },
        "& .MuiInputBase-root>input": {
          padding: [
            "10.5px 10px",
            "10.5px 10px",
            "10.5px 10px",
            "10.5px 10px",
            "16.5px 10px",
            "16.5px 10px",
          ],
        },
        "& .MuiInputLabel-root": {
          transform: {
            md: "translate(10px, 7px) scale(.75)",
            lg: "translate(14px, 16px) scale(1)",
          },
        },
        "& .MuiInputLabel-shrink": {
          transform: {
            md: "translate(13px, -8px) scale(0.75)",
            lg: "translate(13px, -8px) scale(0.75)",
          },
        },
        "& .MuiOutlinedInput-notchedOutline >legend": {
          fontSize: [
            ".75rem",
            ".75rem",
            ".75rem",
            ".75rem",
            ".75rem",
            ".75rem",
          ],
        },
        "& .MuiSvgIcon-root": {
          fontSize: ["1rem", "1rem", "1rem", "1rem", "1.5rem", "1.5rem"],
        },
        backgroundColor: "rgba(0, 0, 0, 0.06)",
      }}
      renderInput={(inProps) => {
        return (
          <TextField
            variant="filled"
            sx={{
              margin: 0,
              "& .MuiInputLabel-shrink": {
                transform: {
                  md: "translate(0px, 7px) scale(0.75)",
                  lg: "translate(0px, 7px) scale(0.75)",
                },
              },
              "& .MuiInputLabel-root": {
                fontSize: [
                  ".5rem",
                  ".5rem",
                  ".8rem",
                  ".8rem",
                  "1.15rem",
                  "1.15rem",
                ],
              },
              "& .MuiInputAdornment-root": {
                mr: ["1rem", ".5rem", ".5rem", ".5rem", "1rem", "1rem"],
              },
            }}
            {...inProps}
            disabled={disabled}
            helperText={computeError(date, inProps.error)}
          />
        );
      }}
      testId={"test"}
    />
  );
};

export const RenderTypography = (inputProps) => {
  const {
    input,
    style,
    label,
    inputProps: extraProps,
    defaultInstitutionId,
  } = inputProps;
  const institutionIdRef = createRef();
  const { onChange } = input;
  const { error = undefined } = inputProps?.meta;
  const [openTooltip, setOpenTooltip] = useState(false);
  return (
    <Box>
      <Typography
        sx={{
          ...specimenSx,
          transform: [
            "translate(0px,-6x)",
            "translate(0px,-6px)",
            "translate(0px,-10px)",
            "translate(0px,-10px)",
            "translate(0px,-15px)",
            "translate(0px,-15px)",
          ],
          fontSize: [".5rem", ".5rem", ".6rem", "0.6rem", "0.9rem", "0.9rem"],
        }}
        style={{
          position: "absolute",
          opacity: "0.75",
          display:
            defaultInstitutionId === input?.value || input?.value === ""
              ? "none"
              : "block",
        }}
        ref={institutionIdRef}
      >
        {defaultInstitutionId}
      </Typography>
      <Tooltip
        onPointerEnter={() => setOpenTooltip(true)}
        onPointerLeave={() => setOpenTooltip(false)}
        open={error || openTooltip}
        title={
          <Typography variant="subtitle1">
            {error ? error : "Add/edit instituiton ID\n"}
          </Typography>
        }
        placement="top"
        PopperProps={
          error
            ? {
                sx: {
                  "& .MuiTypography-root": {
                    color: "#E10050",
                  },
                  "& .MuiTooltip-tooltip": {
                    backgroundColor: "rgba(97, 97, 97, 0.25)",
                  },
                },
              }
            : {}
        }
      >
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <Typography
            {...input}
            onChange={(e, newValue) => {
              onChange(e.target.value);
            }}
            style={{
              ...style,
              display: "inline",
              outline: `0 solid transparent`,
            }}
            label={label}
            {...extraProps}
            contentEditable={true}
            sx={{ ...specimenSx, ...institutionIdSx }}
            suppressContentEditableWarning={true}
            ref={institutionIdRef}
            component="input"
            value={input?.value}
            onBlur={(e) => {
              if (e.target.value === "") {
                onChange(defaultInstitutionId);
              }
            }}
            size={
              input?.value === ""
                ? 12
                : input?.value
                  ? input?.value.length + 2
                  : 5
            }
          ></Typography>
          <EditIcon
            onClick={() => {
              const institutionId = institutionIdRef.current;
              const text = institutionId.value;
              institutionId.focus();
              institutionIdRef.current.setSelectionRange(
                text.length,
                text.length,
              );
            }}
            style={{ display: "inline" }}
            sx={{
              fontSize: [
                "0.85rem",
                "0.85rem",
                "0.85rem",
                "1rem",
                "1.5rem",
                "1.5rem",
              ],
            }}
          />
        </Box>
      </Tooltip>
    </Box>
  );
};
